import { showAlertCallout } from "@/shared/alerts";
import {
  getGetCompanyListQueryKey,
  useGetUsersWithAnyOfPermissions,
} from "@/spz-api/companies/companies";
import { GetUsersWithAnyOfPermissionsPermissionIdsItem } from "@/spz-api/model";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateCompanyDefaultOwner } from "../hooks/use-update-company-default-owner";

const UNASSIGNED_USER = {
  id: "unassigned",
  firstName: "None",
  lastName: "",
  email: "",
  avatarUrl:
    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLXVzZXItaWNvbiBsdWNpZGUtdXNlciI+PHBhdGggZD0iTTE5IDIxdi0yYTQgNCAwIDAgMC00LTRIOWE0IDQgMCAwIDAtNCA0djIiLz48Y2lyY2xlIGN4PSIxMiIgY3k9IjciIHI9IjQiLz48L3N2Zz4=",
} as const;
interface CompanyDefaultOwnerSelectViewModelProps {
  companyId: string;
  defaultOwnerId?: string | null;
}

export const CompanyDefaultOwnerSelectViewModel = ({
  companyId,
  defaultOwnerId,
}: CompanyDefaultOwnerSelectViewModelProps) => {
  const queryClient = useQueryClient();
  const [localOwnerId, setLocalOwnerId] = useState(defaultOwnerId);
  const { t } = useTranslation();

  const { data: usersPerCompany, isPending: usersListPending } =
    useGetUsersWithAnyOfPermissions(companyId, {
      permissionIds: [
        GetUsersWithAnyOfPermissionsPermissionIdsItem["spz:control:view"],
      ],
    });

  const { mutate } = useUpdateCompanyDefaultOwner();

  useEffect(() => {
    setLocalOwnerId(defaultOwnerId);
  }, [defaultOwnerId]);

  const handleSelect = (ids: string[]) => {
    const selectedId = ids[0];
    setLocalOwnerId(selectedId);
    mutate(
      {
        companyId,
        data: {
          newOwnerId:
            selectedId && selectedId !== UNASSIGNED_USER.id ? selectedId : null,
        },
      },
      {
        onSuccess: () => {
          showAlertCallout({
            type: "success",
            title: t("default_owner_update_success"),
          });
          queryClient.invalidateQueries({
            queryKey: getGetCompanyListQueryKey(),
          });
        },
        onError: () => {
          showAlertCallout({
            type: "critical",
            title: t("default_owner_update_error"),
            description: t("generic_error"),
          });
        },
      },
    );
  };

  return {
    localOwnerId,
    usersPerCompany,
    usersListPending,
    handleSelect,
    UNASSIGNED_USER,
  };
};
